import type { NestedFilter } from '@@partner:graphql/types'
import { DateRangeFilter, PriceRangeFilter } from '@@partner:types:app'

export const makeDefaultFilter = (field: string) => ({
  fields: [ field ],
  op: 'unwrap'
})

export const makeBetweenFilter = (field: string, value?: DateRangeFilter) => {
  if (!value) {
    return makeDefaultFilter(field)
  }

  const { from, to } = value

  if (!from && !to) {
    return makeDefaultFilter(field)
  }

  if (from && to) {
    return makeFilter(field, 'between', [ from, to ])
  }
  if (from && !to) {
    return makeFilter(field, 'gte', [ from ])
  }
  if (!from && to) {
    return makeFilter(field, 'lte', [ to ])
  }

  return makeDefaultFilter(field)
}

export const makeNullFilter = (field: string, value: boolean | undefined) => {
  const op =
    value === true ? 'not_null' : value === false ? 'is_null' : 'unwrap'

  return {
    fields: [ field ],
    op
  }
}

export const makeBooleanFilter = (field: string, value: boolean | undefined) => {
  if (typeof value !== 'boolean') {
    return makeDefaultFilter(field)
  }

  return {
    fields: [ field ],
    op: 'eq',
    value: [ value ]
  }
}

export const makeFilter = (
  field: string,
  op: string,
  value: (string | number)[] | (string | number) | undefined | null | boolean
) => {
  if (
    !value ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'string' && value === '') ||
    value === null
  ) {
    return makeDefaultFilter(field)
  }

  const normalizedValue =
    typeof value === 'string' ? value.replaceAll(',', '') : value

  return {
    fields: [ field ],
    op,
    value: Array.isArray(normalizedValue) ? normalizedValue : [ normalizedValue ]
  }
}

export const makeSearchFilter = (field: string, op: string, value?: string) => {
  if (!value || (typeof value === 'string' && value === '')) {
    return makeDefaultFilter(field)
  }

  const normalizedValue =
    typeof value === 'string' ? value.replaceAll(',', '') : value

  return {
    fields: [ field ],
    op,
    value: normalizedValue
  }
}

export const makePriceFilter = (
  field: string,
  value: PriceRangeFilter
): NestedFilter => {
  const filters: NestedFilter[] = []
  const { priceFrom, priceTo } = value

  if (!priceFrom && !priceTo) {
    return makeDefaultFilter(field)
  }

  if (priceFrom) {
    filters.push({ fields: [ field ], op: 'gte', value: [ priceFrom ] })
  }

  if (priceFrom && priceTo) {
    filters.push({
      fields: [ field ],
      op: 'between',
      value: [ priceFrom, priceTo ]
    })
  }

  if (priceTo) {
    filters.push({ fields: [ field ], op: 'lte', value: [ priceTo ] })
  }

  return { and: filters }
}
